import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { useDeleteFile, useFiles } from "src/api/hooks/useFiles.js";
import useGroups from "src/api/hooks/useGroups.js";
import { useProjectVariationById } from "src/api/hooks/useProjectVariationById.js";
import useUsers from "src/api/hooks/useUsers.js";
import { queryKeys } from "src/api/queryKeys.js";
import { updateProjectVariation } from "src/api/updateProjectVariation.js";
import LoadingSpinner from "src/components/LoadingSpinner.js";
import { lang } from "src/language/Strings.js";
import { isMobile } from "src/utils/isMobile.js";
import useDeleteVariation from "src/api/hooks/useDeleteVariation.js";
import { routes } from "src/routes.js";
import useChangeTitle from "src/api/hooks/useChangeTitle";
import { objectTypes } from "src/consts/objectTypes";
import Toggle from "src/components/Toggle";
import useInspectionItems from "src/api/hooks/useInspectionItems";
import { SasFileResult } from "src/types/InspectionUpdateRequest";
import { InspectionUpdateRequest } from "src/types/InspectionUpdateRequest";
import { InspectionStatus } from "src/types/InspectionStatus";
import { InspectionFinishCategory } from "src/types/InspectionFinishCategory";
import FileInput, { FileInputButton } from "../FileInput";
import { ShowToast } from "../Toast";
import { PopupImageGallery } from "src/routes/companyId/Map/Overlays/PopupImageGallery";
import { ImageGallery } from "../ImageGalleryEdit";

export function EditInspectionForm({ id }: { id: string }) {
  const { companyId, deviationId, inspectionId } = useParams();
  const [finishFormVisible, setFinishFormVisible] = useState(false);
  const [defaultDeleteReason, setDefaultDeleteReason] = useState<string | null>(
    null
  );
  const queryClient = useQueryClient();
  const { data: currentItem, invalidate: invalidateCurrentItem } =
    useProjectVariationById(id);
  const { data: files } = useFiles(id);
  const { data: users } = useUsers();
  const { data: groups } = useGroups();

  const ObjectType = useMemo(() => {
    if (
      currentItem?.objectType &&
      (currentItem.objectType === objectTypes.Inspection ||
        String(currentItem?.objectType) === "Egenkontroller")
    ) {
      return objectTypes.Inspection;
    }
    if (inspectionId && deviationId) {
      return objectTypes.Deviation;
    }
    return objectTypes.Deviation;
  }, [currentItem?.objectType]);

  const { data: inspectionItems } = useInspectionItems(
    id || inspectionId || deviationId
  );

  useChangeTitle(
    ObjectType === objectTypes.Inspection
      ? currentItem?.title || currentItem?.title || ""
      : lang.deviationFor(currentItem?.title || currentItem?.title || "") || ""
  );

  const mutation = useMutation({
    mutationFn: handleSubmit,
    mutationKey: [...queryKeys.selectedVariation(deviationId!), "Updated"],
  });

  const putMutation = useMutation({
    mutationFn: updateProjectVariation,
    mutationKey: [...queryKeys.selectedVariation(deviationId!), "Updated"],
  });

  useEffect(() => {
    if (putMutation.status === "success") {
      queryClient.invalidateQueries({
        queryKey: queryKeys.variations(companyId!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.selectedVariation(deviationId!),
      });

      setTimeout(() => {
        invalidateCurrentItem();
        queryClient.invalidateQueries({
          queryKey: queryKeys.files(companyId!, Number(id!)),
        });
      }, 1000);

      setFinishFormVisible(false);

      if (ObjectType === objectTypes.Inspection) {
        navigate(
          routes.Egenkontroller.Pages.Show.url(
            companyId,
            id || inspectionId || deviationId || currentItem?.id!
          )
        );
      } else if (
        ObjectType === objectTypes.Deviation &&
        putMutation.data?.parentDeviationId
      ) {
        navigate(
          routes.Egenkontroller.Pages.Deviations.ShowById.url(
            companyId,
            inspectionId,
            putMutation.data?.parentDeviationId! || deviationId!
          )
        );
      } else {
        navigate(routes.Deviation.Pages.Show.url(companyId, deviationId!));
      }
    }
    if (putMutation.status === "error") {
      console.error(putMutation.error);
      navigate(-1);
    }
  }, [putMutation.status]);

  const deleteMutation = useDeleteVariation();
  const navigate = useNavigate();
  async function handleSubmit(
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;

    const formData = new FormData(form);
    const data: InspectionUpdateRequest = {
      id: currentItem?.id!,
      locationGuid: currentItem?.locationGuid || undefined,
      parentDeviationId: currentItem?.parentDeviationId || undefined,
      deviationType:
        (formData.get("deviationType") as string) ||
        currentItem?.deviationType ||
        undefined,
      status:
        (formData.get("completed") === "on"
          ? InspectionStatus.Finished
          : undefined) ||
        (formData.get("status") as InspectionStatus) ||
        (currentItem?.status as InspectionStatus) ||
        undefined,
      title:
        (formData.get("title") as string) || currentItem?.title || undefined,
      description:
        (formData.get("description") as string) ||
        currentItem?.description ||
        undefined,
      userId: currentItem?.userId || undefined,
      companyId: currentItem?.companyId!,
      category:
        (formData.get("category") as string) || currentItem?.category || "",
      groupId:
        (formData.get("groupId") && Number(formData.get("groupId"))) ||
        currentItem?.groupId ||
        undefined,
      assignedToUserId:
        (formData.get("assignedToUserId") &&
          Number(formData.get("assignedToUserId"))) ||
        currentItem?.assignedToUserID ||
        undefined,
      information:
        (formData.get("information") as string) ||
        currentItem?.information ||
        undefined,
      calculatedPrice:
        (formData.get("calculatedPrice") &&
          Number(formData.get("calculatedPrice"))) ||
        currentItem?.calculatedPrice ||
        undefined,
      customer:
        (formData.get("customer") as string) ||
        currentItem?.customer ||
        undefined,
      objectType: currentItem?.objectType || undefined,
      finishCategory:
        (formData.get("completed") === "on"
          ? InspectionFinishCategory.Completed
          : InspectionFinishCategory.Pending) ||
        (formData.get("finishCategory") as InspectionFinishCategory) ||
        currentItem?.finishCategory ||
        undefined,
      closingRemarks:
        (formData.get("closingRemarks") as string) ||
        currentItem?.closingRemarks ||
        undefined,
    };

    if (String(data.finishCategory) === InspectionFinishCategory.Deleted) {
      await deleteMutation.mutate({ variationId: deviationId! });
      setFinishFormVisible(false);

      await queryClient.invalidateQueries({
        queryKey: queryKeys.variations(companyId!),
      });
    } else {
      putMutation.mutate(data);
    }
  }
  useEffect(() => {
    if (deleteMutation.status === "success") {
      invalidateCurrentItem();
      queryClient.invalidateQueries({
        queryKey: queryKeys.variations(companyId!),
      });
      navigate(routes.Egenkontroller.Pages.List.url(companyId!));
    }
    if (deleteMutation.status === "error") {
      console.error(deleteMutation.error);
      navigate(routes.Deviation.Pages.Show.url(companyId, deviationId!));
    }
  }, [deleteMutation.status]);
  useEffect(() => {
    if (mutation.status === "success") {
      invalidateCurrentItem();
      queryClient.invalidateQueries({
        queryKey: queryKeys.variations(companyId!),
      });
      invalidateCurrentItem();
      if (ObjectType === objectTypes.Inspection) {
        navigate(
          routes.Egenkontroller.Pages.Show.url(companyId, id || inspectionId!)
        );
      } else if (ObjectType === objectTypes.Deviation && inspectionId) {
        navigate(
          routes.Egenkontroller.Pages.Deviations.ShowById.url(
            companyId,
            inspectionId,
            deviationId!
          )
        );
      } else {
        navigate(routes.Deviation.Pages.Show.url(companyId, deviationId!));
      }
    }
    if (mutation.status === "error") {
      console.error(mutation.error);
      if (ObjectType === objectTypes.Inspection) {
        navigate(routes.Egenkontroller.Pages.Show.url(companyId, deviationId!));
      } else if (ObjectType === objectTypes.Deviation && inspectionId) {
        navigate(
          routes.Egenkontroller.Pages.Deviations.ShowById.url(
            companyId,
            inspectionId,
            deviationId!
          )
        );
      } else {
        navigate(routes.Deviation.Pages.Show.url(companyId, deviationId!));
      }
    }
  }, [mutation.status]);

  return (
    <>
      <div className="border-2 border-Grey2 rounded overflow-y-auto h-full">
        <form
          onSubmit={mutation.mutate}
          key={currentItem?.id || "default-key"}
          className="relative"
        >
          {currentItem?.id && (
            <>
              <ItemDescription label={lang.title}>
                <input
                  className="border-2 border-Grey4 rounded-lg pl-2"
                  type="text"
                  name="title"
                  defaultValue={currentItem?.title || ""}
                />
              </ItemDescription>

              <ItemDescription label={lang.description}>
                <textarea
                  title="description"
                  name="description"
                  className="border-2 border-gray-500 rounded-lg pl-2"
                  defaultValue={currentItem?.description || ""}
                />
              </ItemDescription>

              <ItemDescription label={lang.customer}>
                <input
                  className="border-2 border-Grey4 rounded-lg pl-2"
                  type="text"
                  name="customer"
                  defaultValue={currentItem?.customer}
                />
              </ItemDescription>
              <ItemDescription
                label={
                  ObjectType === objectTypes.Inspection
                    ? lang.moment
                    : lang.information
                }
              >
                <textarea
                  title="description"
                  name="information"
                  className="border-2 border-gray-500 rounded-lg pl-2"
                  defaultValue={currentItem?.information || ""}
                />
              </ItemDescription>

              {ObjectType === objectTypes.Deviation && (
                <ItemDescription label={lang.deviationType}>
                  {currentItem?.id && (
                    <select
                      title="deviationTypeId"
                      defaultValue={currentItem?.deviationType || "Other"}
                      name="deviationType"
                      className="border-2 border-Grey4 rounded-lg pt-2 pb-2 w-40"
                    >
                      <option value="Improvement">{lang.improvement}</option>
                      <option value="Urgent">{lang.urgent}</option>
                      <option value="Vandalism">{lang.vandalism}</option>
                      <option value="Other">{lang.other}</option>
                    </select>
                  )}
                </ItemDescription>
              )}
              {ObjectType === objectTypes.Inspection ? (
                <>
                  <ItemDescription label={lang.completed}>
                    <Toggle
                      falseLabel={lang.notCompleted}
                      trueLabel={lang.completed}
                      falseValue="1"
                      trueValue="5"
                      defaultValue={
                        currentItem.status === InspectionStatus.Finished
                          ? "5"
                          : "1"
                      }
                      name="completed"
                    />
                  </ItemDescription>
                  {inspectionItems && inspectionItems.length > 0 && (
                    <ItemDescription label={lang.kontrollmoment}>
                      <div className="flex flex-col">
                        {inspectionItems.map((item) => (
                          <div key={item.id}>
                            <div>{item.title}</div>
                            <div>{item.description}</div>
                          </div>
                        ))}
                      </div>
                    </ItemDescription>
                  )}
                </>
              ) : (
                <ItemDescription label={lang.statusText}>
                  {currentItem?.id && (
                    <select
                      title="title"
                      defaultValue={String(currentItem?.status)}
                      name="status"
                      required
                      className="border-2 border-Grey4 rounded-lg pt-2 pb-2 w-40"
                    >
                      <option value={InspectionStatus.Created}>
                        {lang.created}
                      </option>
                      <option value={InspectionStatus.Pending}>
                        {lang.contactCustomer}
                      </option>
                      <option value={InspectionStatus.Contacted}>
                        {lang.sentToCustomer}
                      </option>
                      <option value={InspectionStatus.Solved}>
                        {lang.sold}
                      </option>
                      <option value={InspectionStatus.Finished}>
                        {lang.finished}
                      </option>
                    </select>
                  )}
                </ItemDescription>
              )}

              <ItemDescription label={lang.group}>
                {currentItem?.id && groups && (
                  <select
                    title="group"
                    name="groupId"
                    defaultValue={String(currentItem.groupId)}
                    className="border-2 border-Grey9 rounded-lg pt-2 pb-2 w-[50%]"
                    required
                  >
                    <option value=""></option>
                    {groups?.map((g) => (
                      <option value={g.id} key={`group-${g.id}`}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                )}
              </ItemDescription>
              {ObjectType === objectTypes.Deviation &&
                currentItem?.id &&
                users && (
                  <ItemDescription label={lang.assignedTo}>
                    {currentItem?.id && (
                      <select
                        title="assignedTo"
                        name="assignedToUserId"
                        defaultValue={
                          currentItem.assignedToUserID !== undefined &&
                          currentItem.assignedToUserID !== null
                            ? String(currentItem.assignedToUserID)
                            : ""
                        }
                        className="border-2 border-Grey9 rounded-lg pt-2 pb-2 w-[50%]"
                      >
                        <option value=""></option>
                        {users?.map((g) => (
                          <option value={g.id} key={`assigneduser-${g.id}`}>
                            {g.firstName + " " + g.surname}
                            {!isMobile() && ", " + g.username}
                          </option>
                        ))}
                      </select>
                    )}
                  </ItemDescription>
                )}
              {finishFormVisible ? (
                <div className="fixed inset-0 flex items-center justify-center bg-White bg-opacity-50 z-50">
                  <div
                    className={`bg-White p-8 rounded-lg shadow-lg ${
                      !isMobile() ? "w-[50%]" : "w-[100%]"
                    } border-2 border-Grey7`}
                  >
                    <h2 className="text-xl font-bold mb-4 text-center">
                      {lang.finishJob}
                    </h2>
                    <ItemDescriptionWithLabelAbove
                      label={lang.finishJobReason}
                      name="finishCategoryLabel"
                    >
                      <select
                        title="finishJobReason"
                        name="finishCategory"
                        defaultValue={
                          currentItem?.finishCategory ||
                          defaultDeleteReason ||
                          undefined
                        }
                        className={`border-2 border-Grey9 rounded-lg pt-2 pb-2 ${
                          !isMobile() ? "w-[50%]" : "w-full"
                        }`}
                      >
                        <option value={InspectionFinishCategory.AlreadyExists}>
                          {lang.alreadyExists}
                        </option>
                        <option value={InspectionFinishCategory.Pending}>
                          {lang.workorderCreated}
                        </option>
                        <option value={InspectionFinishCategory.NotRequired}>
                          {lang.included}
                        </option>
                        <option value={InspectionFinishCategory.Deleted}>
                          {lang.delete}
                        </option>
                        <option value={InspectionFinishCategory.Other}>
                          {lang.other}
                        </option>
                      </select>
                    </ItemDescriptionWithLabelAbove>
                    <ItemDescriptionWithLabelAbove
                      name="closingRemarks-label"
                      label={lang.finishJobComment}
                    >
                      <textarea
                        title="closingRemarks"
                        name="closingRemarks"
                        className="border-2 border-gray-500 rounded-lg pl-2 w-full"
                        defaultValue={currentItem?.closingRemarks || ""}
                      />
                    </ItemDescriptionWithLabelAbove>
                    <div className="w-full justify-center flex">
                      <button
                        type="button"
                        className="bg-White text-white px-4 py-2 rounded-xl hover:bg-Grey2"
                        onClick={() => setFinishFormVisible(false)}
                      >
                        {lang.close}
                      </button>
                      <button
                        type="button"
                        className="ml-4 bg-White text-white px-4 py-2 rounded-xl hover:bg-Grey2"
                        onClick={() => {
                          deleteMutation.mutate({
                            variationId: Number(id!),
                          });
                        }}
                      >
                        {lang.save}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                ""
              )}
              <ItemDescription label={lang.save}>
                <button
                  type="button"
                  className="rounded-xl p-2 mr-2 border-2 border-Grey4 text-PrimaryRed bg-Grey1"
                  onClick={() => {
                    setDefaultDeleteReason(InspectionFinishCategory.Deleted);
                    setFinishFormVisible(true);
                  }}
                >
                  {lang.delete}
                </button>
                <button
                  type="submit"
                  className="rounded-xl p-2 border-2 border-Grey4 bg-Grey1"
                >
                  {lang.save}
                </button>
              </ItemDescription>
            </>
          )}
        </form>
        <div className="w-full p-2">
          <div className="w-full p-5 flex flex-row scroll-x-auto overflow-x-auto">
            <ImageGallery 
            editMode={true}
            maxHeight="h-80"
            images={currentItem?.files || []} />

          </div>
        </div>
      </div>
      {mutation.isPending && <LoadingSpinner />}
    </>
  );
}

function ItemDescription({ label, children }) {
  return (
    <div className="border-b-2 border-Grey2 p-2 w-full min-h-10 flex flex-row text-left">
      <div className="w-2/6 h-full flex justify-start items-center pl-8">
        {label}
      </div>
      <div className="w-4/6 h-full border-left-2 border-Grey2 flex justify-start items-center mt-1 pl-8">
        {children}
      </div>
    </div>
  );
}

function ItemDescriptionWithLabelAbove({ label, children, name }) {
  return (
    <div className="border-b-2 border-Grey2 p-2 w-full min-h-10 flex flex-row ">
      <div className="w-full h-full border-left-2 border-Grey2 flex flex-col mt-1">
        <label htmlFor={name} className="">
          {label}:
        </label>
        {children}
      </div>
    </div>
  );
}

function DownloadedImage({ file }: { file: SasFileResult }) {
  const [deleteItemOpen, setDeleteItemOpen] = useState(false);
  const deleteMutation = useDeleteFile(Number(file.id!));
  const { deviationId, inspectionId } = useParams();
  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    deleteMutation.mutate();
  };

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteItemOpen(false);
  };
  const queryClient = useQueryClient();
  useEffect(() => {
    if (deleteMutation.status === "success") {
      ShowToast(lang.fileDeletedSuccessfully, "success");
      queryClient.invalidateQueries({
        queryKey: queryKeys.selectedVariation(deviationId || inspectionId!),
      });
      setDeleteItemOpen(false);
    }
    if (deleteMutation.status === "error") {
      console.error(deleteMutation.error);
      ShowToast(lang.fileDeletedError, "error");
      setDeleteItemOpen(false);
    }
  }, [deleteMutation.status]);

  return (
    <>
      {file.imageResponse?.urlOriginal && (
        <div className="rounded-lg relative select-none">
          <i
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDeleteItemOpen(true);
            }}
            className="fa-solid fa-times top-2 right-4 absolute text-PrimaryRed hover:scale-110 transform transition-all duration-200 ease-in-out hover:font-bold hover:text-xl cursor-pointer"
          />
          <img
            src={file.imageResponse?.urlOriginal || ""}
            title={file.fileName}
            className={`h-52 max-w-60 mr-2 p-2 border-Grey2 rounded-2 border-r-2 object-contain bg-[url(${
              file.imageResponse?.urlx32 ||
              file.imageResponse?.urlx240 ||
              file.imageResponse?.urlx1080 ||
              ""
            })] bg-cover bg-center`}
          />
        </div>
      )}
      {deleteItemOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black opacity-50"
            onClick={handleCancel}
          />
          <div
            id="alert-window"
            className="relative w-80 h-60 bg-White border-2 border-primary-black rounded-lg flex flex-col items-center justify-center p-4"
          >
            <p className="text-lg mb-4 text-center">
              {lang.removeImageMessage}
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleCancel}
                className="border-Grey4 border-2 px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                type="button"
              >
                {lang.cancel}
              </button>
              <button
                onClick={handleDelete}
                className="border-Grey4 border-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                type="button"
              >
                {lang.delete}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
